<template>
  <v-layout column fill-height class="back">
    <scroll-area>
      <template slot="header">
        <v-toolbar dense flat light class="back">
            <v-toolbar-title class="body-2 font-weight-medium text-uppercase">{{$t('files')}}</v-toolbar-title>
            <v-spacer></v-spacer>
            <v-tooltip dark color="secondary" left>
              <v-btn :data-testid="$options.name + 'v-btn' + 'onFileEdit'" v-if="editable && files.length" color="grey lighten-5" small icon @click="onFileEdit" slot="activator">
                <v-icon small>edit</v-icon>
              </v-btn>
              <span>{{ $t('edit') }}</span>
            </v-tooltip>
            <v-tooltip dark color="secondary" left>
              <v-btn :data-testid="$options.name + 'v-btn' + 'add'" color="grey lighten-5" small icon slot="activator">
                <v-icon>add</v-icon>
                <input v-if="!clear" ref="file-input" class="button-file-upload__input" type="file" title=" " multiple accept @change="onFileAdd" >
              </v-btn>
              <span>{{ $t('add') }}</span>
            </v-tooltip>
        </v-toolbar>
      </template>

      <template slot="body">
        <v-list v-if="getFiles.length">
          <template v-for="(file, index) in getFiles">
            <v-list-tile
              :key="index + file.name"
              avatar
              ripple
              dense
            >
                <v-list-tile-title>
                  <span
                    class="body-1 font-weight-medium font-condensend black--text"
                    :title="file.data.description"
                    style="cursor: pointer"
                    @click.stop=""
                  >
                    {{file.name}}
                  </span>
                </v-list-tile-title>

                <v-list-tile-sub-title :title="file.data.description" v-if="file.data.description">{{ showDescription ? file.data.description : ''}}</v-list-tile-sub-title>
                <v-list-tile-sub-title v-else>{{ showDescription ? $t('no description') : ''}}</v-list-tile-sub-title>

                <v-tooltip left color="transparent">
                  <v-btn :data-testid="$options.name + 'v-btn' + 'onFileDownload-fileindex'" flat icon small ripple slot="activator" @click.stop="onFileDownload(file, index)" color="transparent">
                    <span class="fiv-viv button-file-upload__control" :class="['fiv-size-xl', 'fiv-icon-' + `${ checkFile(file.name.split('.').pop().toLowerCase())}`]"></span>
                  </v-btn>
                  <span class="blacktext--text">{{ $t('download file') }}</span>
                </v-tooltip>

              <v-list-tile-action>
                <v-flex shrink px-2>
                  <v-tooltip left color="secondary">
                    <v-btn :data-testid="$options.name + 'v-btn' + 'onFileDeleteConfirm-file'" icon small ripple slot="activator" @click="onFileDeleteConfirm(file)">
                      <v-icon color="red">delete</v-icon>
                    </v-btn>
                    <span>{{ $t('delete file') }}</span>
                  </v-tooltip>
                </v-flex>
              </v-list-tile-action>
            </v-list-tile>

            <v-divider
              v-if="index + 1 < getFiles.length"
              :key="index"
            ></v-divider>

          </template>
        </v-list>
      </template>
    </scroll-area>

    <v-dialog v-if="dialog.confirm" v-model="dialog.confirm" persistent max-width="290">
      <v-card>
        <v-card-title class="blue lighten-3 caption">
          <v-layout fill-height justify-space-around>
            <v-flex shrink>
              {{$t('confirmation request')}}
            </v-flex>
          </v-layout>
        </v-card-title>
        <v-card-text>{{$t('are you sure you want to delete')}} {{ dialog.file.name }}?</v-card-text>
        <v-card-actions>
          <v-layout px-2>
            <v-btn :data-testid="$options.name + 'v-btn' + 'dialogfile--null-dialogconfirm--false'" small block color="grey" @click="dialog.file = null; dialog.confirm = false">{{$t('no')}}</v-btn>
            <v-btn :data-testid="$options.name + 'v-btn' + 'onFileDelete-dialogfileid'" small block color="error" @click="onFileDelete(dialog.file.id)">{{$t('yes')}}</v-btn>
          </v-layout>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script lang="ts">
import Vue from 'vue'
import scrollArea from '@/nms/components/partials/scrollarea.vue'
import { getURL } from '@/nms/plugins/bridge.js'

// Типы для файлов
interface FileItem {
  name: string
  url?: string
  id?: string
  [key: string]: any
}

interface DialogState {
  confirm: boolean
  file: FileItem | null
}

interface DownloadState {
  progress: number | null
  total: number | null
}

interface UploadState {
  files: any[]
}

export default Vue.extend({
  name: 'FILE_LIST',

  components: {
    scrollArea,
  },

  props: {
    files: {
      type: Array as () => FileItem[],
      default: () => []
    },
    data: {
      type: Object as () => any,
      default: () => ({})
    },
    editable: {
      type: Boolean,
      default: () => true
    },
    showDescription: {
      type: Boolean,
      default: () => true
    },
  },

  watch: {
    files: {
      handler(newVal: FileItem[]): void {
        console.log('WATCH', 'FILE_LIST', 'files', newVal)
      },
      deep: true
    },
  },

  computed: {
    getFiles(): FileItem[] {
      return this.files
    }
  },

  data(): {
    clear: boolean
    upload: UploadState
    dialog: DialogState
    download: DownloadState
  } {
    return {
      clear: false,
      upload: {
        files: []
      },
      dialog: {
        confirm: false,
        file: null
      },
      download: {
        progress: null,
        total: null,
      },
    }
  },

  created(): void {},

  methods: {
    onFileAdd(e: Event): void {
      const target = e.target as HTMLInputElement
      if (!target.files?.length) return
      this.$emit('upload', target.files)
      this.clear = true
      this.$nextTick(() => {
        this.clear = false
      })
    },

    filepath(name: string): string {
      return require(`@/nms/components/utils/file/styles/icons/vivid/${name}.svg`)
    },

    checkFile(name: string): string {
      try {
        const url = this.filepath(name)
        if (url) {
          return name
        } else {
          return 'default'
        }
      } catch (error) {
        return 'default'
      }
    },

    onFileDownload(file: FileItem, index: number): void {
      let lastLoadSize = 0
      
      this.$http({
        method: 'get',
        url: file.url,
        responseType: 'arraybuffer',
        onDownloadProgress: (progressEvent: any) => {
          this.download.progress = progressEvent.loaded
          this.download.total = progressEvent.total
          if (progressEvent.loaded < 1000000) return
          
          const toast = this.$snotify.get('toast-' + file.name)
          if (!toast) {
            this.$snotify.html(
              '<div class="fit"><span class="text-truncate block">' + file.name + '</span></div>' +
              '<div class="caption fit"><span id="toast-progress-text-' + file.name + '" class="text-truncate block">Загружено <span id="toast-progress-value-' + file.name + '">' + progressEvent.loaded + '</span> bytes</span></div>',
              {
                id: 'toast-' + file.name,
                timeout: 0,
                showProgressBar: false,
                closeOnClick: true,
                pauseOnHover: false,
              }
            )
          } else {
            const el = document.getElementById('toast-progress-value-' + file.name)
            if (el) {
              el.innerText = progressEvent.loaded
            }
          }
        }
      })
      .then((response: any) => {
        this.$snotify.remove('toast-' + file.name)
        
        if (!response || response.status !== 200) {
          throw new Error('Не удалось загрузить файл')
        }
        
        const fileExtension = file.name.substr(file.name.length - 3).toLowerCase()
        if (fileExtension === 'pdf') {
          this.showFile(response.data, file.name)
        } else {
          this.saveBlobToFile(response.data, file.name)
        }
        
        this.$snotify.success('Файл ' + file.name + ' успешно загружен!', {
          timeout: 2000,
          showProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
        })
      })
      .catch((error: any) => {
        console.error('Ошибка загрузки файла:', error)
        
        this.$snotify.remove('toast-' + file.name)
        
        if (error.response && error.response.status === 404) {
          this.$snotify.error('Файл "' + file.name + '" не найден на сервере (404)', {
            timeout: 3000,
            showProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
          })
        } else if (error.response && error.response.status === 403) {
          this.$snotify.error('Нет доступа к файлу "' + file.name + '" (403)', {
            timeout: 3000,
            showProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
          })
        } else if (error.code === 'ECONNABORTED') {
          this.$snotify.error('Превышено время ожидания при загрузке файла "' + file.name + '"', {
            timeout: 3000,
            showProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
          })
        } else {
          this.$snotify.error('Ошибка загрузки файла "' + file.name + '": ' + (error.message || 'Неизвестная ошибка'), {
            timeout: 3000,
            showProgressBar: false,
            closeOnClick: true,
            pauseOnHover: true,
          })
        }
      })
    },

    saveBlobToFile(strData: any, strFileName: string, strMimeType?: string): void {
      const fileAsBlob = new Blob([strData], { type: strMimeType || 'application/octet-stream' })
      const downloadLink = document.createElement('a')
      downloadLink.download = strFileName
      downloadLink.innerHTML = 'Download File'
      
      if (window.URL != null) {
        downloadLink.href = window.URL.createObjectURL(fileAsBlob)
        downloadLink.onclick = () => downloadLink.remove()
        downloadLink.style.display = 'none'
        document.body.appendChild(downloadLink)
      } else {
        downloadLink.href = window.URL.createObjectURL(fileAsBlob)
        downloadLink.onclick = () => downloadLink.remove()
        downloadLink.style.display = 'none'
        document.body.appendChild(downloadLink)
      }
      downloadLink.click()
    },

    showFile(blob: any, strFileName: string): void {
      const newBlob = new Blob([blob], { type: 'application/pdf' })
      
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(newBlob)
        return
      } else {
        const data = window.URL.createObjectURL(newBlob)
        const newWin = window.open(data)
        if (newWin) {
          newWin.focus()
          newWin.reload()
        }
      }
    },

    onFileEdit(): void {
      this.$emit('edit')
    },

    onFileDeleteConfirm(file: FileItem): void {
      this.dialog.file = file
      this.dialog.confirm = true
    },

    onFileDelete(id: string): void {
      this.$emit('delete', id)
      this.dialog.file = null
      this.dialog.confirm = false
    },
  },
})
</script>

<style lang="stylus" scoped>

    .button-file-upload

        position: relative
        box-sizing: border-box

        &_extension

            &_blank

                padding-left: 0;
                border-color: transparent

            &_new

                padding-left: 0;
                border-color: transparent

        &__area

            display: flex
            justify-content: center

            width: 80px
            height: 80px

            border-radius: 50%
            border: 1px solid alpha(#000, .4)

        &__control

            position: relative
            overflow: hidden
            z-index: 2

            cursor: pointer

            &.fiv-icon-blank

                opacity: .4
                transition: opacity .4s

                &:hover

                    opacity: 1

            &.fiv-icon-new

                opacity: .4
                transition: opacity .4s

                &:hover

                    opacity: 1

        &__overlay

            position: absolute

            top: 0
            right: 0
            bottom: 0
            left: 0

            margin-right: -8px

            width: 100%
            /* height */

            background: alpha(#fff, .9)

            opacity: 0
            transition: opacity .4s

            &:hover

                opacity: 1

        &__delete

            cursor: pointer

            margin-right: -16px
            margin-top: -16px

            opacity: .5
            transition: opacity .4s

            &:hover

                opacity: 1

        &__input

            display: block
            position: absolute

            top: 0
            right: 0

            width: 100%
            height: 100%
            /* min-width: 100%
            min-height: 100% */

            text-align: right

            opacity: 0
            outline: none
            cursor: inherit

        &__name

            display: block /* Fallback for non-webkit */
            max-height: calc(20px * 2) /* Fallback for non-webkit */
            min-height: calc(20px * 2) /* Fallback for non-webkit */
            margin: 0 auto
            font-size: 12px
            line-height: 16px
            -webkit-line-clamp: 3
            -webkit-box-orient: vertical
            overflow: hidden
            text-overflow: ellipsis
            word-break: break-all
            text-align: center;


</style>
